import React, { useState, useEffect, useCallback, useRef } from 'react';
import GameBoard from './GameBoard';
import HUD from './HUD';
import Mallet from './Mallet';
import Controls from './Controls';
import GameOverlay from './GameOverlay';
import TimeSelector from './TimeSelector';
import './Game.css';

const AnomaWhackAMole = () => {
  const [score, setScore] = useState(0);
  const [missed, setMissed] = useState(0);
  const [hits, setHits] = useState(0); // Track successful hits
  const [timeLeft, setTimeLeft] = useState(30);
  const [gameActive, setGameActive] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const [overlayTitle, setOverlayTitle] = useState('Whack a Anoma');
  const [overlayMessage, setOverlayMessage] = useState('Click to start the game!');
  const [activeMoles, setActiveMoles] = useState(new Set());
  const [moleCharacters, setMoleCharacters] = useState(new Map()); // Track character type per hole
  const [isPaused, setIsPaused] = useState(false);
  const [targetHole, setTargetHole] = useState(null); // Track which hole to point mallet at
  const [showTimeSelector, setShowTimeSelector] = useState(false);
  const [selectedTime, setSelectedTime] = useState(30);

  const gameIntervalRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const malletRef = useRef(null);
  const scoreRef = useRef(0);
  const missedRef = useRef(0);
  const hitsRef = useRef(0);

  // Device detection
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Sync refs with state for accurate endGame values
  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  useEffect(() => {
    missedRef.current = missed;
  }, [missed]);

  useEffect(() => {
    hitsRef.current = hits;
  }, [hits]);

  const characterTypes = [
    'udang1',      // Udang - Basic
    'anomas1',     // Anomas - Basic
    'anomapenyihir1', // Anoma Penyihir - Basic
    'anoma1',      // Anoma Basic 1 - Random points
    'anoma2',      // Anoma Basic 2 - Random points
    'anoma3',      // Anoma Basic 3 - Random points
    'anoma4',      // Anoma Basic 4 - Random points
    'shrimpers',   // SHRIMPERS - High random points
    'shrimpers1',  // SHRIMPERS1 - High random points
    'shrimpers2',  // SHRIMPERS2 - High random points
    'shrimpers3'   // SHRIMPERS3 - High random points
  ];

  const showOverlayHandler = useCallback((title, message) => {
    setOverlayTitle(title);
    setOverlayMessage(message);
    setShowOverlay(true);
  }, []);

  const hideOverlay = useCallback(() => {
    setShowOverlay(false);
  }, []);

  const handleBackToMenu = useCallback(() => {
    setShowTimeSelector(false);
    setShowOverlay(true);
  }, []);

  const handlePlayAgain = useCallback(() => {
    setShowOverlay(false);
    setShowTimeSelector(true);
  }, []);

  const getAccuracy = useCallback(() => {
    const totalAttempts = hits + missed;
    if (totalAttempts === 0) return 0;
    return Math.round((hits / totalAttempts) * 100);
  }, [hits, missed]);

  const endGame = useCallback(() => {
    setGameActive(false);
    setIsPaused(false);
    if (gameIntervalRef.current) {
      clearInterval(gameIntervalRef.current);
      gameIntervalRef.current = null;
    }
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    setActiveMoles(new Set());
    setMoleCharacters(new Map());
    
    // Use ref values for accurate final scores
    const finalScore = scoreRef.current;
    const finalMissed = missedRef.current;
    const finalHits = hitsRef.current;
    const totalAttempts = finalHits + finalMissed;
    const accuracy = totalAttempts === 0 ? 0 : Math.round((finalHits / totalAttempts) * 100);
    
    const message = `Score: ${finalScore} Missed: ${finalMissed} Accuracy: ${accuracy}%`;
    showOverlayHandler('Game Over!', message);
  }, [showOverlayHandler]);

  const spawnMole = useCallback(() => {
    if (!gameActive || isPaused) return;

    setActiveMoles(prev => {
      const availableHoles = [];
      for (let i = 0; i < 12; i++) {
        if (!prev.has(i)) {
          availableHoles.push(i);
        }
      }

      if (availableHoles.length === 0) return prev;

      // Randomly select from available holes
      const randomHole = availableHoles[Math.floor(Math.random() * availableHoles.length)];
      const newActiveMoles = new Set(prev);
      newActiveMoles.add(randomHole);

      // Select random character type for this mole
      const randomCharacterType = characterTypes[Math.floor(Math.random() * characterTypes.length)];
      
      // Store character type for this hole
      setMoleCharacters(prevChars => {
        const newChars = new Map(prevChars);
        newChars.set(randomHole, randomCharacterType);
        return newChars;
      });

      // Auto-hide after random time (1-2.5 seconds)
      const hideTime = Math.random() * 1500 + 1000;
      setTimeout(() => {
        setActiveMoles(current => {
          const updated = new Set(current);
          // Only count as missed if the mole is still active (wasn't hit)
          if (current.has(randomHole)) {
            setMissed(prev => prev + 1);
          }
          updated.delete(randomHole);
          return updated;
        });
        setMoleCharacters(currentChars => {
          const updated = new Map(currentChars);
          updated.delete(randomHole);
          return updated;
        });
      }, hideTime);

      return newActiveMoles;
    });
  }, [gameActive, isPaused, characterTypes]);

  const startGame = useCallback(() => {
    hideOverlay();
    setShowTimeSelector(true);
  }, [hideOverlay]);

  const handleTimeSelect = useCallback((time) => {
    setSelectedTime(time);
    setScore(0);
    setMissed(0);
    setHits(0);
    scoreRef.current = 0;
    missedRef.current = 0;
    hitsRef.current = 0;
    setTimeLeft(time);
    setGameActive(true);
    setActiveMoles(new Set());
    setMoleCharacters(new Map());
    setIsPaused(false);
    setShowTimeSelector(false);
    setTargetHole(null);

    // Start timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    
    timerIntervalRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          endGame();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [hideOverlay, endGame]);

  // useEffect for spawning moles - this is the main spawn system
  useEffect(() => {
    if (gameActive && !isPaused) {
      const spawnRate = isMobile ? 800 : 600;
      const interval = setInterval(() => {
        spawnMole();
      }, spawnRate);
      
      return () => clearInterval(interval);
    }
  }, [gameActive, isPaused, isMobile, spawnMole]);

  const togglePause = useCallback(() => {
    if (!gameActive) return;

    if (isPaused) {
      // Resume game
      setIsPaused(false);
      hideOverlay();
      const spawnRate = isMobile ? 800 : 600;
      gameIntervalRef.current = setInterval(() => spawnMole(), spawnRate);
      timerIntervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            endGame();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      // Pause game
      setIsPaused(true);
      if (gameIntervalRef.current) {
        clearInterval(gameIntervalRef.current);
        gameIntervalRef.current = null;
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      showOverlayHandler('Game Paused', 'Click resume to continue!');
    }
  }, [gameActive, isPaused, isMobile, hideOverlay, showOverlayHandler, endGame]);

  const showHelp = useCallback(() => {
    const helpMessage = `How to Play:
        
🎯 Click on the characters to whack them!
⏰ You have 30 seconds
🪙 Each hit gives you points
💀 Missing characters costs lives
🎮 Use the Anoma Palu for bonus points!

Characters & Points:
🦐 Cool Shrimp - Random 10-59 points
👤 Anoma King - Random 10-59 points
🧙 Anoma Wizard - Random 10-59 points
🦐 Anoma Shrimp - Random 10-59 points
🦐 Anoma Cool - Random 10-59 points
🦐 Anoma Magic - Random 10-59 points
🦐 Anoma Party - Random 10-59 points
🦐 Shrimpster - Random 60-100 points (HIGHEST!)
🦐 Shrimp Boss - Random 60-100 points (HIGHEST!)
🦐 Shrimp Master - Random 60-100 points (HIGHEST!)
🦐 Shrimp King - Random 60-100 points (HIGHEST!)

Good luck!`;
    
    showOverlayHandler('How to Play', helpMessage);
  }, [showOverlayHandler]);

  const whackMole = useCallback((holeIndex) => {
    if (!gameActive || isPaused) return;

    // Set target hole for mallet to move to
    setTargetHole(holeIndex);

    // Check if there's an active mole in this hole
    if (!activeMoles.has(holeIndex)) {
      // Missed hit - clicked on empty hole
      setMissed(prev => prev + 1);
      swingMallet();
      // Reset target after mallet moves and swings
      setTimeout(() => setTargetHole(null), 1500);
      return;
    }

    // Get the character type for this specific hole
    const characterType = moleCharacters.get(holeIndex) || 'udang1';
    
    // Debug logging (remove in production)
    // console.log(`Whacking ${characterType} in hole ${holeIndex}`);
    
    setActiveMoles(prev => {
      const updated = new Set(prev);
      updated.delete(holeIndex);
      return updated;
    });

    setMoleCharacters(prev => {
      const updated = new Map(prev);
      updated.delete(holeIndex);
      return updated;
    });

    // Add score based on actual character type
    let points;
    if (characterType.startsWith('shrimpers')) {
      // SHRIMPERS characters get higher random points (60-100)
      points = Math.floor(Math.random() * 41) + 60;
    } else {
      // Other characters get normal random points (10-59)
      points = Math.floor(Math.random() * 50) + 10;
    }

    setScore(prev => prev + points);
    setHits(prev => prev + 1); // Increment successful hits
    swingMallet();
    
    // Reset target after mallet moves and swings
    setTimeout(() => setTargetHole(null), 1500);
  }, [gameActive, isPaused, activeMoles, moleCharacters]);

  const swingMallet = useCallback(() => {
    if (malletRef.current) {
      malletRef.current.classList.add('swing');
      setTimeout(() => {
        if (malletRef.current) {
          malletRef.current.classList.remove('swing');
        }
      }, 300);
    }
  }, []);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch(e.key) {
        case ' ':
          e.preventDefault();
          if (gameActive) {
            togglePause();
          } else {
            startGame();
          }
          break;
        case 'h':
          showHelp();
          break;
        case 'Escape':
          if (gameActive) {
            togglePause();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [gameActive, togglePause, startGame, showHelp]);


  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (gameIntervalRef.current) {
        clearInterval(gameIntervalRef.current);
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  return (
    <div className="game">
      <HUD score={score} missed={missed} timeLeft={timeLeft} />
      
      <GameBoard 
        activeMoles={activeMoles}
        onWhackMole={whackMole}
        characterTypes={characterTypes}
        moleCharacters={moleCharacters}
      />

      <Mallet ref={malletRef} onSwing={swingMallet} targetHole={targetHole} />

      <Controls 
        onPause={togglePause}
        onHelp={showHelp}
        isPaused={isPaused}
      />

      {showOverlay && (
        <GameOverlay
          title={overlayTitle}
          message={overlayMessage}
          onStart={overlayTitle === 'Game Over!' ? handlePlayAgain : 
                   overlayTitle === 'Game Paused' ? togglePause : startGame}
          onClose={hideOverlay}
        />
      )}

      {showTimeSelector && (
        <TimeSelector
          onTimeSelect={handleTimeSelect}
          onBack={handleBackToMenu}
        />
      )}
    </div>
  );
};

export default AnomaWhackAMole;
